import type { Dictionary } from "./types";

export const zhHant = {
  common: {
    signIn: "登入",
    signOut: "登出",
    settings: "設定",
    help: "說明",
    add: "新增",
    account: "帳戶",
    cancel: "取消",
    apply: "套用",
    dismiss: "關閉",
    retry: "重試",
    save: "儲存",
    loading: "載入中",
    openWorkspace: "打開工作台",
    language: "語言",
    signInWith: (provider) => `使用 ${provider} 登入`
  },
  dataTable: {
    rows: (count) => `${count} 列`,
    columns: "欄位"
  },
  entities: {
    person: "人物",
    organisation: "公司",
    event: "事件",
    opportunity: "商機",
    note: "筆記",
    personSingular: "聯絡人",
    organisationSingular: "公司",
    eventSingular: "事件",
    opportunitySingular: "商機",
    noteSingular: "筆記",
    nodeFallback: "節點"
  },
  nav: {
    overview: "總覽",
    graph: "圖譜",
    people: "人物",
    companies: "公司",
    events: "事件",
    schedule: "行程",
    signals: "商業",
    current: "目前",
    privateAgent: "助理",
    workspace: "工作台",
    connectors: "連接器"
  },
  login: {
    tagline: "每一次對話，都沉澱成一張活的關係圖譜。",
    headingIn: "歡迎回來",
    headingOut: "登入 Salesko",
    subIn: "你的工作階段已就緒。",
    subOut: "使用你的 Salesko 帳戶進入工作台。",
    subOutUnavailable: "Salesko 帳戶登入尚未設定。",
    continueWith: (provider) => `使用 ${provider} 繼續`,
    openWithout: "不登入直接進入",
    notConfigured: "登入方式尚未設定。",
    fineLead: "繼續即表示你同意我們的",
    terms: "服務條款",
    privacy: "隱私政策",
    and: "和",
    period: "。"
  },
  workspace: {
    contactsIntelligenceWorkspaceAriaLabel: "Salesko 聯絡人智能工作台",
    headerAriaLabel: "工作台標頭",
    searchPlaceholder: "搜尋人物、公司、事件…",
    searchAriaLabel: "搜尋圖譜",
    searchResultsLabel: "圖譜搜尋結果",
    tenantControlsAriaLabel: "租戶控制",
    opportunitySignalsAriaLabel: "商機訊號",
    canonicalRegistryAriaLabel: "標準實體登錄",
    selectedEntityAriaLabel: "已選實體",
    detailPanelToggleAriaLabel: "切換詳情面板",
    deleteConversationConfirm: "刪除此對話？此操作無法復原。",
    currentCount: (count) => `目前 ${count} 筆`,
    savedCount: (count) => `已存 ${count} 筆`,
    searchingSaved: "搜尋已存",
    noCurrentMatch: "目前畫面沒有符合的節點。",
    savedFrames: "已存畫面",
    canonicalMatches: "標準比對結果",
    unavailable: "無法使用",
    searchingSavedFrames: "正在搜尋已存畫面…",
    noSavedMatch: "已存畫面沒有符合的節點。",
    viewsLabel: "檢視",
    accountMenuLabel: "帳戶選單",
    noProviderConfigured: "尚未設定登入方式",
    quotaUnlimited: "無限",
    quotaUnlimitedTooltip: "你的方案可無限使用 Agent 訊息",
    quotaRemaining: (remaining, limit) =>
      `今日 Agent 訊息剩 ${remaining} / ${limit} 則 · 00:00 UTC 重置`
  },
  accountSurface: {
    ariaLabel: "帳戶",
    syncCopy: "同步畫面、訊號與租戶存取。",
    tenantLabel: (tenantId) => `租戶 ${tenantId}`,
    noProviderConfigured: "尚未設定登入方式。",
    sessionGrantReady: "Session grant 已就緒"
  },
  authProviders: {
    signInOptions: "登入選項"
  },
  workspaceError: {
    eyebrow: "工作台錯誤",
    title: "Salesko 圖譜工作台無法渲染。",
    fallback: "工作台遇到未預期的渲染錯誤。",
    retry: "重試工作台"
  },
  graphStage: {
    graphFieldAriaLabel: "圖譜區域",
    relationshipGraphLabel: "關係圖譜",
    viewModeLabel: "圖譜檢視模式",
    show2dTitle: "顯示 Obsidian 風格的 2D 力導向圖",
    timelineEnabledTitle: "依時間排列事件",
    timelineDisabledTitle: "至少需要 2 個事件才能建立時間線",
    createShareAriaLabel: "建立唯讀分享連結",
    sharedReadOnlyTitle: "共享檢視為唯讀",
    copyShareTitle: "複製唯讀分享連結",
    openSettingsAriaLabel: "開啟工作台設定",
    openSettingsTitle: "開啟設定",
    openGuideAriaLabel: "開啟工作台指南",
    openGuideTitle: "開啟工作台指南",
    toggleFullscreenAriaLabel: "切換圖譜全螢幕",
    toggleFullscreenTitle: "切換全螢幕",
    loadingGraphField: "正在載入圖譜區域…",
    agentFrameWaiting: "正在等待 Agent 畫面。",
    graphNodesAriaLabel: "圖譜節點",
    nodeEdgeCount: (nodeCount, edgeCount) => `${nodeCount} 個節點、${edgeCount} 條關係。`,
    projectedGraphCount: (visibleNodeCount, sourceNodeCount, visibleEdgeCount, sourceEdgeCount) =>
      `目前顯示 ${visibleNodeCount}/${sourceNodeCount} 個節點、${visibleEdgeCount}/${sourceEdgeCount} 條關係。`,
    layoutDegraded: "背景佈局最佳化無法使用，目前顯示即時佈局。",
    selectNode: (label) => `選取 ${label}`,
    selectRelationship: (label) => `選取關係 ${label}`,
    flatGraphAriaLabel: "2D 力導向圖",
    timelineGraphAriaLabel: "時間線關係圖",
    timelineLabel: "時間線",
    timelineMomentsAriaLabel: "時間線事件",
    previousTimelineMoment: "上一個事件",
    nextTimelineMoment: "下一個事件",
    timelinePosition: (position, total) => `第 ${position}/${total} 個事件`,
    dateUnknown: "日期未知",
    noLinkedEntities: "暫無關聯實體",
    linkedEntityCount: (count) => `${count} 個關聯實體`,
    timelineNeedsTwoEvents: "時間線至少需要兩個事件。",
    addDatedInteraction: "新增有日期的互動",
    setOpeningMomentTitle: "將此事件儲存為畫面的開啟時刻",
    setOpeningMoment: "設為開啟時刻",
    graphControlsAriaLabel: "圖譜檢視控制",
    recenter: "重新置中",
    zoomIn: "放大",
    zoomOut: "縮小",
    navigatorAriaLabel: "圖譜導航器。拖曳以旋轉圖譜。",
    warningsCount: (count) => ` ${count} 則警告。`
  },
  frameIo: {
    dismissStatus: "關閉畫面匯入/匯出/分享狀態",
    exporting: (format) => `正在匯出 ${format} artifact。`,
    importing: (fileName, format) => `正在將 ${fileName} 匯入為新的 ${format} 畫面。`,
    importingSimple: (fileName) => `正在匯入 ${fileName}。`,
    creatingShareLink: "正在建立唯讀分享連結。",
    creatingShareLinkShort: "正在建立分享連結。",
    downloaded: (format) => `已下載 ${format} artifact`,
    downloadedFile: (filename) => `已下載 ${filename}。`,
    copiedShareLink: "已複製唯讀分享連結",
    shareLinkReadyNoCopy: "唯讀分享連結已就緒；瀏覽器無法複製",
    shareLinkReady: "分享連結已就緒。",
    shareExpires: (date) => `到期日 ${date}。`,
    importedSummary: (fileName, nodeCount, edgeCount) =>
      `已匯入 ${fileName}：${nodeCount} 個節點、${edgeCount} 條關係。`,
    importedOpening: (fileName) => `已匯入 ${fileName}。正在開啟畫面。`,
    fileFallback: "檔案",
    obsidianFilesLabel: (count) => `${count} 個 Obsidian 檔案`,
    errors: {
      exportFailed: "畫面匯出失敗。",
      shareLinkFailed: "無法建立分享連結。",
      importFailed: "畫面匯入失敗。",
      exportNeedsCommittedFrame: "請先生成並提交圖譜畫面，再匯出。",
      shareNeedsCommittedFrame: "請先生成並提交圖譜畫面，再分享。",
      jsonImportFailed: "JSON 匯入失敗。",
      obsidianImportFailed: "Obsidian 匯入失敗。"
    },
    fileErrors: {
      jsonTooLarge: "JSON 圖譜匯入上限為 5 MB。",
      jsonInvalid: (fileName) => `${fileName} 不是有效 JSON。`,
      jsonInvalidShape: "JSON 匯入檔案必須是 Salesko 圖譜匯出或關係圖譜檔案。",
      obsidianEmptySelection: "請至少選擇一個 Obsidian Markdown 檔案或匯出 artifact。",
      obsidianJsonTooLarge: "Obsidian JSON artifact 匯入上限為 5 MB。",
      obsidianJsonInvalidShape: "Obsidian JSON 匯入檔案必須是 Salesko Obsidian 圖譜匯出。",
      obsidianMarkdownTooLarge: (fileName) => `${fileName} 超過 100 KB Obsidian 檔案上限。`
    }
  },
  changeSet: {
    dismissStatus: "關閉變更集狀態",
    importingFile: (fileName) => `正在將 ${fileName} 匯入為待處理變更集。`,
    creatingFor: (sourceLabel) => `正在為 ${sourceLabel} 建立待處理變更集。`,
    pendingSummary: (changeSetId, sourceLabel, commandCount, nodeCount, edgeCount) =>
      nodeCount === undefined || edgeCount === undefined
        ? `待處理變更集 ${changeSetId}（${sourceLabel}）：${commandCount} 個 commands。`
        : `待處理變更集 ${changeSetId}（${sourceLabel}）：${commandCount} 個 commands、${nodeCount} 個節點、${edgeCount} 條關係。`,
    applying: (changeSetId, sourceLabel) => `正在套用變更集 ${changeSetId}（${sourceLabel}）。`,
    reverting: (changeSetId, sourceLabel) => `正在還原變更集 ${changeSetId}（${sourceLabel}）。`,
    applied: (changeSetId, projectionStatus) => `已套用變更集 ${changeSetId}；projection ${projectionStatus}。`,
    reverted: (changeSetId, projectionStatus) => `已還原變更集 ${changeSetId}；projection ${projectionStatus}。`,
    apply: "套用",
    revert: "還原",
    pendingPanel: (changeSetId, sourceLabel) => `待處理變更集 ${changeSetId} 來自 ${sourceLabel}。`,
    updatingSource: (action, sourceLabel) => `${action} ${sourceLabel}。`,
    sourceUpdated: (sourceLabel) => `${sourceLabel} 已更新。正在開啟畫面。`,
    errors: {
      historyFailed: "變更集歷史載入失敗。",
      auditLogFailed: "變更集 audit log 載入失敗。",
      auditExportFailed: "Audit 匯出失敗。",
      csvImportFailed: "CSV 匯入失敗。",
      createFailed: "建立變更集失敗。",
      applyFailed: "套用變更集失敗。",
      revertFailed: "還原變更集失敗。",
      csvNeedsCommittedFrame: "請先生成並提交圖譜畫面，再匯入 CSV。",
      createNeedsCommittedFrame: "請先生成並提交圖譜畫面，再建立圖譜變更。",
      csvTooLarge: "此 API slice 的 CSV 匯入上限為 100 KB。",
      csvEmpty: "CSV 檔案是空的。"
    },
    confirm: {
      csvImport: (fileName) => `將 ${fileName} 匯入為目前圖譜畫面的待處理變更集？`,
      create: (sourceLabel) => `為 ${sourceLabel} 建立待處理變更集？`,
      apply: (summary) => `將 ${summary} 套用到此圖譜畫面？`,
      applyImportSummary: (commandCount, sourceLabel) => `${commandCount} 個 commands，來源 ${sourceLabel}`,
      applyChangeSetSummary: (changeSetId) => `變更集 ${changeSetId}`,
      revert: (changeSetId, sourceLabel) => `還原 ${sourceLabel} 的變更集 ${changeSetId}？`
    },
    sources: {
      nodeEdit: (label) => `節點編輯：${label}`,
      relationship: (sourceLabel, targetLabel) => `關係：${sourceLabel} -> ${targetLabel}`,
      relationshipEdit: (label) => `關係編輯：${label}`,
      relationshipDelete: (label) => `關係刪除：${label}`,
      relationshipTouchpoint: (label) => `關係 touchpoint：${label}`,
      timelineReplay: (label) => `時間線 replay：${label}`,
      leadStageMoveIn: (label) => `移入目前：${label}`,
      leadStageMoveOut: (label) => `移出目前：${label}`,
      targetFallback: "目標",
      currentMoment: "目前時刻"
    }
  },
  leftRail: {
    entitiesLabel: "實體",
    toggleEntity: (label) => `切換 ${label}`,
    relationshipStrengthLabel: "畫面關係強度",
    strengthWeak: "弱",
    strengthMedium: "中",
    strengthStrong: "強",
    recentFrames: "最近畫面",
    loadingFrames: "正在載入已提交畫面…",
    frameHistoryUnavailable: "畫面紀錄目前無法使用。",
    noFramesYet: "已提交的畫面會顯示在這裡。",
    frameNodeCount: (count) => `${count} 個節點`,
    frameLoadError: (frameId) => `無法載入畫面 ${frameId}。`,
    recentConversations: "最近對話",
    newConversationAriaLabel: "開始新對話",
    newConversationTitle: "新對話",
    loadingConversations: "正在載入對話…",
    conversationHistoryUnavailable: "對話紀錄目前無法使用。",
    noConversationsYet: "對話會顯示在這裡。",
    threadMessageCount: (count) => `${count} 則訊息`,
    deleteConversationTitle: "刪除對話",
    deleteConversationAriaLabel: (title) => `刪除對話 ${title}`,
    conversationLoadError: (threadId) => `無法載入對話 ${threadId}。`
  },
  onboarding: {
    eyebrowGuide: "工作台指南",
    heading: "從一張圖譜畫面開始",
    graphSummary: (nodeCount, edgeCount) => `目前檢視 ${nodeCount} 個節點 · ${edgeCount} 個關係`,
    firstGraphSectionLabel: "第一張圖譜流程",
    eyebrowFirstGraph: "第一張圖譜",
    captureContextTitle: "擷取脈絡",
    captureContextBody: "將會議記錄、通話記錄或商機脈絡貼進 Agent 輸入框。",
    reviewGraphTitle: "檢視圖譜",
    reviewGraphBody: "檢視候選項目、編輯節點或關係，再套用待處理的變更。",
    keepFrameTitle: "保留畫面",
    keepFrameBody: "登入後的工作台可從最近畫面還原已提交的畫面。",
    openAgent: "開啟 Agent",
    startInGraph: "直接進入圖譜",
    workspaceStatusLabel: "工作台狀態",
    signedInStatus: "已登入",
    signInToPersistStatus: "登入以保留畫面",
    frameHistoryActiveStatus: "畫面紀錄已啟用",
    noCommittedFrameStatus: "尚未提交任何畫面",
    readOnlySharedViewStatus: "唯讀共享檢視",
    workspaceEditableStatus: "工作台可編輯",
    signInOptionsLabel: "工作台登入選項"
  },
  agentChat: {
    graphChat: "Graph Chat",
    openAgentInputAriaLabel: "開啟 Agent 輸入框",
    agentInputAriaLabel: "Agent 輸入",
    headline: "把脈絡變成圖譜",
    subtitle: "免費 · 即時 · 使用你提供的脈絡",
    signedInTitle: "已登入",
    signInOptionsAriaLabel: "登入選項",
    collapseAgentInputAriaLabel: "收合 Agent 輸入框",
    collapseTitle: "收合",
    agentConversationAriaLabel: "Agent 對話",
    exampleNoteAriaLabel: "範例說明",
    exampleOnly: "僅供範例",
    useExample: "用這個範例",
    seeExample: "看個範例",
    messageLabel: "訊息",
    messagePlaceholder: "貼上筆記，或請 Graph Chat 更新圖譜，也可輸入 @ 提及實體…",
    mentionListAriaLabel: "提及圖譜實體",
    mentionWithText: (text) => `提及「${text}」`,
    mentionCurrentEntity: "提及目前圖譜實體",
    noMentionMatches: "目前畫面沒有符合的實體。",
    composerHint: "人物、事件、公司、商機",
    generating: "生成中…",
    send: "傳送",
    structuringGraphFrame: "正在結構化圖譜畫面",
    requestFailed: "請求失敗",
    needsReview: "待審核",
    executed: "已執行",
    nodesLabel: "節點",
    edgesLabel: "關係",
    peopleLabel: "人物",
    eventsLabel: "事件",
    sourceModel: "模型",
    sourceRules: "規則",
    sourceAgent: "Agent",
    genericAgentName: "Salesko Agent",
    genericAgentSubtitle: "跑在 Salesko 上",
    handoff: (agentName) => `移交給 ${agentName}`,
    streamStructuring: "正在用 Graph Chat 結構化你的關係脈絡。",
    streamAuthRequired: "此筆記儲存前，Graph Chat 需要模型抽取。",
    streamRebuilding: "正在讀取脈絡並重建聯絡人圖譜。",
    streamSeekerStarting: "正在唯讀查看你的圖譜，準備和你聊聊。",
    previewStreamFailed: "Agent 預覽串流失敗。",
    noteIntakeExtractionFailure: "Agent LLM 抽取未產生 graph frame；筆記未持久化。",
    noteIntakeExtractionFailureReason: (reasons) => `原因：${reasons}`,
    noteIntakeCapturing: "正在透過 intake pipeline 擷取筆記。",
    modeToggleAriaLabel: "對話模式",
    modeWriterLabel: "寫作",
    modeSeekerLabel: "探討",
    modeWriterHint: "寫作模式：這段對話會更新你的圖譜。",
    modeSeekerHint: "探討模式：唯讀討論，不會修改圖譜。",
    seekerMessagePlaceholder: "讓探討模式陪你聊聊、分析這張關係圖譜——唯讀，不會做任何修改…"
  },
  detail: {
    emptyTitle: "尚未選擇聯絡人或實體",
    selfMarkedTitle: "此節點已標記為你；點擊可清除",
    selfMarkTitle: "將此節點標記為你（圖譜的 ego-center）",
    selfYou: "你",
    selfSet: "設為你",
    leadStageInTitle: "將此節點標記為目前追蹤對象",
    leadStageOutTitle: "此節點已在目前中；點擊可移出",
    leadStageInLabel: "移入目前",
    leadStageOutLabel: "已在目前",
    dismissCalendarEvent: "忽略此事件",
    dismissCalendarEventPending: "正在忽略…",
    dismissCalendarEventDone: "已從圖譜中忽略此事件。",
    dismissCalendarEventError: "無法忽略此行事曆事件，請重試。",
    about: "關於",
    followUp: "跟進",
    contact: "聯絡方式",
    relationshipContext: "關係背景",
    personalDetails: "個人資料",
    relationshipStageField: "關係階段",
    relationshipSourceField: "關係來源",
    contactActionsAriaLabel: "聯絡操作",
    emailAction: "發電郵",
    callAction: "打電話",
    staleByDays: (days) => `已逾期 ${days} 天`,
    noAboutYet: "還沒有簡介。",
    source: "來源",
    editNodeTitle: "編輯選取節點",
    commitBeforeEditNodesTitle: "編輯節點前請先提交畫面",
    editNodeAriaLabel: "編輯選取節點",
    labelField: "標籤",
    summaryField: "摘要",
    evidenceField: "證據",
    roleField: "角色",
    companyField: "公司",
    stageField: "階段",
    sourceField: "來源",
    timeTextField: "時間文字",
    genderField: "性別",
    birthdayField: "生日",
    interestsField: "興趣喜好",
    emailField: "電郵",
    phoneField: "電話",
    locationField: "所在地",
    industryField: "行業",
    sizeField: "公司規模",
    revenueField: "年營收",
    websiteField: "官網",
    fieldOptionUnset: "未填寫",
    genderOptions: {
      male: "男",
      female: "女",
      other: "其他",
      undisclosed: "不願透露"
    },
    industryOptions: {
      technology: "科技",
      finance: "金融",
      healthcare: "醫療健康",
      manufacturing: "製造業",
      retail: "零售",
      education: "教育",
      media: "媒體",
      realEstate: "房地產",
      energy: "能源",
      consulting: "諮詢",
      government: "政府 / 公共",
      other: "其他"
    },
    saveAsChangeSet: "儲存為變更集",
    canonicalIdentityAriaLabel: "標準身份",
    identity: "身份",
    editIdentityTitle: "編輯標準身份",
    commitBeforeEditIdentityTitle: "編輯身份前請先提交畫面",
    editIdentityAriaLabel: "編輯標準身份",
    identityActionRunning: (action, sourceLabel) => `${action} ${sourceLabel}`,
    identityActionSaved: (action) => `${action} 已儲存`,
    splitLabel: "拆分標籤",
    splitSelected: "拆分選取項",
    rekeyLabel: "重設鍵標籤",
    rekeyIdentity: "重設身份鍵",
    mergeTarget: "合併目標",
    noSameTypeTarget: "沒有同類型目標",
    signalsPrefix: (signals) => `訊號：${signals}`,
    mergeIdentity: "合併身份",
    reviewCandidateAriaLabel: "審核候選項",
    review: "審核",
    reviewCount: (count) => (count === 1 ? "1 個候選項需要審核" : `${count} 個候選項需要審核`),
    extractionConfidence: "抽取信心",
    reviewReasonAgentFlagged: "Agent 已標記",
    reviewReasonLowConfidence: (percent) => `${percent}% 信心`,
    accept: "接受",
    discard: "捨棄",
    linkedEntities: "已連結實體",
    viewAllLinkedTitle: (label) => `在標準登錄中檢視所有 ${label}`,
    viewAllLinkedAriaLabel: (count, label) => `在標準登錄中檢視 ${count} 個 ${label}`,
    viewAllLinked: (count) => `檢視全部 ${count}`,
    createRelationshipTitle: "建立關係",
    commitBeforeCreateRelationshipTitle: "請先提交至少含兩個節點的畫面",
    createRelationshipAriaLabel: "建立關係",
    targetField: "目標",
    typeField: "類型",
    relationshipTypeRelatedTo: "相關於",
    relationshipTypeMetAt: "見於",
    relationshipTypeWorksAt: "任職於",
    relationshipTypeAttended: "參與",
    relationshipTypeIntroducedBy: "由其介紹",
    relationshipTypeOpportunityAt: "商機所在",
    relationshipTypeMentioned: "提及",
    strengthField: (percent) => `強度 ${percent}%`,
    saveRelationship: "儲存關係",
    editingRelationship: (endpoint, edgeId) => `正在編輯 ${endpoint} · ${edgeId}`,
    saveRelationshipChanges: "儲存關係變更",
    existingRelationshipsAriaLabel: "既有關係",
    editRelationshipAriaLabel: (label) => `編輯關係 ${label}`,
    deleteRelationshipAriaLabel: (label) => `刪除關係 ${label}`,
    editRelationshipTitle: "編輯關係",
    commitBeforeEditRelationshipsTitle: "編輯關係前請先提交畫面",
    deleteRelationshipTitle: "刪除關係",
    commitBeforeDeleteRelationshipsTitle: "刪除關係前請先提交畫面",
    linkedGroupsAriaLabel: "已連結實體分組",
    noLinkedYet: (label) => `尚未連結 ${label}。`,
    frameCommit: "畫面提交",
    sharedStatus: "已共享",
    previewStatus: "預覽",
    idleStatus: "閒置",
    readOnlyGraphFrame: "唯讀圖譜畫面",
    defaultGraphFrame: "預設圖譜畫面",
    waitingForGraphFrame: "等待圖譜畫面",
    advanced: "進階",
    relationshipDirectionOut: "由此實體發出",
    relationshipDirectionIn: "指向此實體",
    identityActionMerge: "合併",
    identityActionRekey: "重設鍵",
    identityActionSplit: "拆分"
  },
  relationshipIntel: {
    ariaLabel: "關係智慧",
    title: "關係智慧",
    signInCommittedFrame: "登入並開啟已提交畫面後，才能載入關係智慧。",
    addAnotherEntity: "新增另一個實體以檢查介紹路徑。",
    targetLabel: "介紹路徑目標",
    pathsTo: (label) => `通往 ${label} 的路徑`,
    introductionPaths: "介紹路徑",
    loadingPaths: "正在載入介紹路徑",
    pathsUnavailable: "關係路徑目前無法使用。",
    noPath: "此組合找不到介紹路徑。",
    commonNeighbor: "共同鄰居",
    direct: "直接",
    seenInFrames: (count) => `出現在 ${count} 個畫面`,
    staleReminders: "冷卻關係提醒",
    loadingStale: "正在載入冷卻關係",
    remindersUnavailable: "關係提醒目前無法使用。",
    noStaleForEntity: (count) => `此實體沒有冷卻關係；此租戶其他位置有 ${count} 個。`,
    noStaleOlderThan: (days) => `沒有超過 ${days} 天的冷卻關係。`,
    lastSeenStaleBy: (lastSeen, days) => `最後出現 ${lastSeen} · 冷卻 ${days} 天`,
    recordTouchpointTitle: "將 touchpoint 記錄為待處理變更集",
    openLastSeenFrameTitle: "開啟此關係最後出現的畫面",
    logTouchpoint: "記錄 touchpoint",
    openInFrame: "在畫面中開啟"
  },
  changeSetHistory: {
    ariaLabel: "變更集紀錄",
    title: "變更集",
    refreshAriaLabel: "重新整理變更集紀錄",
    refreshTitle: "重新整理變更集紀錄",
    noCommit: "生成並提交畫面後才能查看 mutation 紀錄。",
    loading: "正在載入變更集紀錄",
    empty: "此畫面尚無變更集紀錄。",
    downloaded: (filename) => `已下載 ${filename}`,
    auditLog: "Audit log",
    auditLogFor: (changeSetId) => `${changeSetId} 的 audit log`,
    loadingAuditLog: "正在載入 audit log",
    emptyAuditLog: "此變更集沒有 audit log 事件。",
    graphMutation: "圖譜 mutation",
    commandCount: (count) => `${count} 個 commands`,
    exportAuditAriaLabel: (changeSetId) => `匯出 ${changeSetId} 的 audit artifact`,
    inspectAuditAriaLabel: (changeSetId) => `檢查 ${changeSetId} 的 audit log`,
    exportAuditTitle: "匯出 audit JSON",
    auditLogTitle: "Audit log",
    columnChangeSet: "變更集",
    columnStatus: "狀態",
    columnCommands: "Commands",
    columnUpdated: "更新時間",
    columnActions: "操作",
    columnEvent: "事件",
    columnCreated: "建立時間",
    columnDetails: "詳情",
    auditCreated: "已建立",
    auditApplied: "已套用",
    auditReverted: "已還原",
    privateAgentSource: "Private Agent"
  },
  signals: {
    title: "商機收件箱",
    eyebrow: "跨全部已儲存圖譜紀錄",
    inboxSummary: (pending, saved) => `目前顯示：${pending} 個待處理 · ${saved} 個已儲存`,
    unavailable: "商機收件箱無法使用",
    viewModeAriaLabel: "商機收件箱檢視模式",
    cardViewTitle: "卡片檢視",
    tableViewTitle: "表格檢視",
    cards: "卡片",
    table: "表格",
    signInToLoad: "登入以載入你的私人商機收件箱。",
    loading: "正在載入商機收件箱…",
    empty: "目前沒有待處理或已儲存的商機。",
    actionsFor: (title) => `${title} 的操作`,
    openSourceFor: (title) => `開啟 ${title} 的來源畫面`,
    openSourceFrame: "開啟來源畫面",
    updated: "更新時間",
    evidence: "證據",
    source: "來源",
    confidence: (score) => `信心分數 ${score}%`,
    whySurfaced: "出現原因",
    followUpRequested: (date) => `已加入未排程跟進 ${date}`,
    details: "詳情",
    reasonUnavailable: "此商機沒有記錄出現原因。",
    evidenceAvailable: "可查看",
    evidenceUnavailable: "未提供",
    evidenceSpanCount: (count) => `${count} 個 evidence spans`,
    relatedEntities: "相關實體",
    relatedFrame: "相關畫面",
    sourceAvailable: "可開啟來源畫面",
    sourceUnavailable: "未提供",
    suggestedNextAction: "建議下一步",
    status: {
      pending: "待處理",
      saved: "已儲存",
      dismissed: "已忽略",
      archived: "已封存"
    },
    actions: {
      dismiss: "忽略",
      save: "儲存商機",
      saved: "已儲存",
      followUp: "加入未排程跟進",
      followUpAdded: "已加入跟進"
    },
    columns: {
      actions: "操作",
      evidence: "證據",
      score: "信心分數",
      source: "來源",
      status: "狀態",
      title: "標題",
      updatedAt: "更新時間"
    }
  },
  activityFeed: {
    surfaceAriaLabel: "商業動態與商機",
    closeSurface: "關閉商業面板",
    tabsAriaLabel: "商業功能",
    activityTab: "活動紀錄",
    inboxTab: "商機收件箱",
    title: "活動紀錄",
    eyebrow: "已記錄的事實與變更",
    pageSummary: (count, hasNext) => hasNext ? `顯示最近 ${count} 條紀錄` : `共 ${count} 條紀錄`,
    unavailable: "活動紀錄無法使用",
    loading: "正在載入活動紀錄…",
    empty: "暫時沒有已記錄的活動。",
    recordedAt: "記錄時間",
    occurredAt: "發生時間",
    timeUnavailable: "未記錄",
    backfilled: "歷史補錄",
    backfilledLabel: "是否補錄",
    backfilledValue: (backfilled) => backfilled ? "是" : "否",
    correction: "更正",
    supersedes: "取代一條較早記錄",
    evidence: "證據",
    evidenceStates: {
      available: "可查看",
      sourceUnavailable: "來源無法使用",
      redacted: "已遮蓋"
    },
    entryTypes: {
      eventOccurred: "事件發生",
      opportunitySurfaced: "商機出現",
      transactionRecorded: "交易已記錄",
      transactionStatusChanged: "交易狀態已變更",
      transactionCorrected: "交易已更正"
    },
    subjectTypes: {
      event: "事件",
      opportunity: "商機",
      transaction: "交易"
    },
    inspectSource: (index) => `查看來源 ${index}`,
    inspectSourceFor: (title, index) => `查看 ${title} 的來源 ${index}`,
    inspectTransaction: "查看交易",
    inspectTransactionFor: (title) => `查看 ${title} 的交易詳情`,
    openTransactionGraph: "在圖譜中開啟",
    openTransactionGraphFor: (title) => `在圖譜中開啟：${title}`,
    loadOlder: "載入較早紀錄",
    loadingOlder: "正在載入較早紀錄…",
    sourceDetailTitle: "精確來源",
    transactionDetailTitle: "目前交易",
    transactionGraphTitle: "交易圖譜",
    transactionGraphReadOnly: "目前事實及其有證據支持的連結 · 唯讀",
    closeSourceDetail: "關閉來源詳情",
    closeTransactionDetail: "關閉交易詳情",
    closeTransactionGraph: "關閉交易圖譜",
    loadingSource: "正在載入精確來源…",
    loadingTransaction: "正在載入目前交易…",
    loadingTransactionGraph: "正在載入交易圖譜…",
    transactionGraphUnavailable: "交易圖譜暫時無法使用。",
    selectTransactionGraphNode: "選擇一個節點以查看其證據。",
    exactQuote: "原文引句",
    evidenceSpan: "證據區段",
    sourceFrame: "來源畫面",
    sourceNode: "來源節點",
    openSourceFrame: "開啟來源畫面",
    kind: "類型",
    externalId: "外部 ID",
    status: "狀態",
    amount: "金額",
    parties: "相關方",
    provenance: "來源紀錄",
    acceptedAt: "接受時間",
    evidenceText: "證據",
    authority: "權威來源",
    sourceFact: "來源事實",
    sourceFrames: "來源畫面",
    evidenceSpans: "證據區段",
    sourceReferences: "來源引用",
    transactionKinds: {
      contract: "合約",
      order: "訂單",
      invoice: "發票",
      payment: "付款"
    },
    transactionStatuses: {
      committed: "已確認",
      settled: "已結清",
      cancelled: "已取消"
    }
  },
  settings: {
    navigationAriaLabel: "設定導覽",
    sectionsAriaLabel: "設定區段",
    title: "設定",
    workspaceSettings: "工作台設定",
    description: "集中管理你的帳戶、Agent 預設值與資料。",
    signOut: "登出",
    billingSuccess: "方案已啟用。Deep Research 現已可用；Agent credits 已加入。",
    billingCancel: "Checkout 已取消。你的方案未變更。",
    sidebar: {
      account: "帳戶",
      accountDescription: "命名你的工作台",
      billing: "Billing",
      billingDescription: "Messaging addon 方案",
      credits: "Credits",
      creditsDescription: "Agent credit 餘額",
      computers: "電腦",
      computersDescription: "連接執行 agent 的那台電腦",
      privateAgent: "Private Agent",
      privateAgentDescription: "命名、綁定電腦、選擇 runtime",
      agent: "Agent",
      agentDescription: "語言、區域、管道",
      data: "資料",
      dataDescription: "匯出工作台資料",
      advanced: "進階",
      advancedDescription: "檔案匯入與畫面 artifacts"
    }
  },
  billing: {
    ariaLabel: "Billing",
    title: "Messaging channels",
    signIn: "登入以管理 messaging addon。",
    active: "Messaging addon 已啟用",
    pendingCancel: "將於週期結束時取消",
    renews: (date) => `續訂日 ${date}`,
    upsell: "Telegram、WeChat 與 Line delivery 已包含於任何付費方案。",
    viewPlans: "查看方案"
  },
  credits: {
    ariaLabel: "Agent credits",
    title: "Agent credits",
    signIn: "登入以查看 Agent credits。",
    upgrade: "升級付費方案以解鎖 Deep Research。",
    byok: "Deep Research 在你自己的電腦上執行，用你自己的 provider 登入狀態或 key；不消耗 Salesko credits。",
    available: "可用 credits",
    empty: "尚無 credit activity。",
    loadError: "無法載入 credit 餘額。請稍後再試。"
  },
  computers: {
    ariaLabel: "電腦",
    title: "電腦",
    description: "Private Agent 跑在你在這裡連接的電腦上。Salesko 負責協調，運算在你的電腦。",
    signIn: "登入以連接電腦。",
    connect: "連接一台電腦",
    stepInstallTitle: "1 · 安裝並配對",
    stepInstallBody: "先建立一次性配對碼，再到目標 Mac 貼上產生的連接指令。它會一次完成安裝、配對與背景啟動。",
    stepInstallPrereq: "僅支援 macOS（Apple 晶片或 Intel）。安裝腳本會挑選對應這台 Mac 的建置，驗證官方發布的 checksum 之後再安裝。",
    stepInstallGuideLink: "開啟安裝指南",
    setupExisting: "已經安裝？這條 setup 指令會完成配對並設定背景啟動。",
    updateTitle: "更新 Agent · 保留配對",
    updateBody: "在已配對電腦的終端執行，安裝最新版本並重新啟動背景服務。保留現有配對，無需產生新的配對碼。",
    startupTitle: "啟動設定",
    startupBody: "在已經配對的電腦上執行這條指令，可修復或更新背景服務。",
    createPairingCode: "建立配對碼",
    copy: "複製",
    copied: "已複製",
    pairingCodeExpiresAt: (expiresAt) => `配對碼 ${expiresAt} 到期`,
    stepWaitingTitle: "2 · 等待電腦上線",
    stepWaitingBody: "執行上面那條指令。電腦一回報就會出現在這裡。",
    emptyTitle: "還沒有連接電腦",
    empty: "建立配對碼，連接 Private Agent 要執行的那台電腦。",
    presenceIsHint: "Presence 是短時 TTL hint，不代表 readiness 或已執行。",
    presenceHint: (level, expiresAt) => `${level} hint，至 ${expiresAt}`,
    noPresenceHint: "暫無 presence hint",
    online: "在線",
    offline: "離線",
    installedRelease: (version) => `本機版本：${version}`,
    installedReleaseUnavailable: "本機版本：未回報",
    latestRelease: (version) => `最新版本：${version}`,
    latestReleaseLoading: "最新版本：載入中",
    latestReleaseUnavailable: "最新版本：暫時無法取得",
    runtimes: "偵測到的 runtime",
    noRuntimeFacts: "這台電腦還沒回報它有哪些 runtime。",
    signedIn: "已登入",
    notSignedIn: "未登入",
    revoked: "已撤銷",
    revoke: "撤銷",
    keysStayOnComputer: "Provider 登入狀態與 API key 都留在電腦上。在那台電腦執行：",
    updateError: "無法更新你的電腦清單。請稍後再試。"
  },
  privateAgent: {
    ariaLabel: "Private Agent",
    title: "Private Agent",
    description: "你自己的 agent，綁定一台電腦，用你自己的 runtime 登入狀態。Salesko 看不到這些憑證。",
    signIn: "登入以設定 Private Agent。",
    loading: "正在載入 Private Agent",
    name: "Agent 名稱",
    namePlaceholder: "給你的 agent 取個名字",
    computer: "電腦",
    computerHint: "Agent 只執行在一台已連接的電腦上。",
    runtime: "Runtime",
    runtimeHint: "不管選哪個，登入狀態都留在你的電腦上。",
    piVendor: "廠商預設",
    piVendorHint: "選擇廠商後會列出該廠商的 model，並產生本機設定指令。下方設定 ID 必須與電腦上的設定一致。",
    piVendorPlaceholder: "選擇廠商",
    piConfigureHint: "在 Agent 電腦上執行。API key 在終端隱藏輸入並儲存在本機；此指令將該設定設為裝置預設設定。",
    subscriptionModel: "模型",
    runtimeDefaultModel: "使用 runtime 預設模型",
    specifyModel: "指定模型",
    modelId: "模型代號或 ID",
    subscriptionModelHint: "輸入該 Agent 和帳號支援的模型代號或 ID。代號對應的版本可能隨時間更新；不可用的模型會在執行時回報錯誤。",
    piProvider: "Pi 設定 ID",
    piProviderHint: "填寫 salesko-agent keys list 回傳的準確 profile_ref，而非廠商名稱。",
    piProviderPlaceholder: "openrouter",
    piModel: "Pi model",
    piModelHint: "從所選廠商公布的 model 中選擇；請先選擇廠商。",
    piModelPlaceholder: "選擇 model",
    runtimes: {
      claudeSubscription: "Claude 訂閱",
      codexSubscription: "Codex 訂閱",
      piProviderKey: "Pi + 你的 provider key"
    },
    researchPreset: "研究 preset",
    presetHint: "這是同一個 research agent 的區域 preset，不是多個 agent。",
    profiles: {
      default: "預設",
      mainland: "中國大陸 (CN)",
      taiwan: "台灣 (TW)",
      hongKong: "香港 (HK)"
    },
    save: "儲存 Private Agent",
    saved: "Private Agent 已儲存。",
    projectionPending: "正在等待綁定的電腦套用這份 Profile。",
    projectionSynced: "綁定的電腦已套用這份 Profile。",
    projectionFailed: "綁定的電腦無法套用這份 Profile。",
    remove: "刪除 agent",
    removeConfirm: "刪除這個 Private Agent？在重新建立之前研究任務會停。",
    removeConfirmAction: "刪除",
    removeCancel: "先留著",
    deviceNotActive: "那台電腦已經不可用了。請選一台已連接的。",
    needsRebindingTitle: "需要重新綁定",
    needsRebinding: "這個 agent 綁定的電腦已被撤銷或不存在。選另一台再儲存。",
    noComputersTitle: "還沒有連接電腦",
    noComputers: "先連接一台電腦——Private Agent 總得有地方跑。",
    goToComputers: "去連接電腦",
    loadError: "無法載入 Private Agent。請稍後再試。",
    saveError: "無法儲存 Private Agent。請稍後再試。"
  },
  privateAgentJobs: {
    ariaLabel: "Private Agent 研究任務",
    eyebrow: "Private Agent",
    title: "Private Agent",
    signIn: "登入後查看 Private Agent 正在做什麼。",
    loading: "正在載入 Private Agent",
    loadingJobs: "正在載入研究任務",
    loadError: "無法載入研究任務，請稍後再試。",
    emptyTitle: "還沒有研究任務",
    empty: "打開一個人物或商機，讓 Private Agent 去研究。",
    noAgentTitle: "還沒有 Private Agent",
    noAgent: "先給 agent 命名並綁定一台已連接的電腦，它才能開始研究。",
    setUpAgent: "去設定 Private Agent",
    openComputers: "打開 Computers",
    computerOffline: "執行這個 agent 的電腦目前離線。排隊中的任務會在它上線後開始。",
    computerRevoked: "這個 agent 綁定的電腦已被撤銷。連接一台電腦並重新綁定。",
    computer: {
      online: "電腦在線",
      offline: "電腦離線",
      revoked: "電腦已撤銷",
      missing: "未綁定電腦"
    },
    status: {
      queued: "排隊中",
      dispatching: "派發中",
      running: "執行中",
      cancelRequested: "取消中",
      succeeded: "待審閱",
      failed: "失敗",
      canceled: "已取消",
      waitingDevice: "等待你的電腦",
      erased: "已清除"
    },
    statusDetail: {
      waitingDevice: "沒有已連接的電腦接下這次研究。連接一台後重新發起。",
      queuedComputerOffline: "排隊中 \u2014 電腦離線",
      queuedNoComputer: "排隊中 \u2014 沒有已連接的電腦"
    },
    stopReasons: {
      completed: "已完成",
      stepBudget: "達到步數預算",
      timeBudget: "達到時間預算",
      tokenBudget: "達到 token 預算",
      providerLimit: "被 provider 限額中斷"
    },
    stopReason: (label) => `停止原因：${label}`,
    startedAt: (when) => `開始於 ${when}`,
    jobId: (id) => `任務 ${id}`,
    review: "審閱變更",
    reviewPending: "準備中\u2026",
    cancel: "取消",
    canceling: "取消中\u2026",
    showDetails: "詳情",
    hideDetails: "收起詳情",
    loadingDetails: "正在載入詳情",
    noResultDetail: "這個任務沒有回傳結果。",
    frameConflict: "研究開始後圖譜已經變了。請在目前的圖譜上重新發起。",
    resultNotReady: "這次研究還沒有可審閱的結果。",
    materializeError: "無法產生變更集，請稍後再試。",
    changeSetSourceLabel: (agentName) => `${agentName} 的研究`,
    question: (entityLabel) => `研究 ${entityLabel}，並提出關係圖譜的更新建議。`,
    trigger: {
      ariaLabel: "Private Agent 研究",
      eyebrow: "Private Agent",
      action: (agentName) => `讓 ${agentName} 研究`,
      actionFallback: "讓 Private Agent 研究",
      starting: "正在發起\u2026",
      checkingAccess: "正在確認你的方案\u2026",
      signedOut: "登入後才能在自己的電腦上跑研究。",
      entitlementRequired: "Deep Research 包含在付費方案裡。",
      profileRequired: "先給 Private Agent 命名並綁定一台已連接的電腦。",
      noActiveComputer: "先連接一台電腦給 Private Agent 執行。",
      activeJob: "Private Agent 已經在跑一個研究任務。",
      activeJobConflict: "Private Agent 已經在跑一個研究任務。等它結束，或者先取消。",
      noCommittedFrame: "先提交一份圖譜。研究是針對已儲存的圖譜提變更。",
      viewPlans: "查看方案",
      openJobs: "打開助理",
      insufficientCredits: "Agent credits 不足，無法發起研究。請儲值或升級方案。",
      frameVersionUnavailable: "這份圖譜沒有版本號可供研究。重新整理後再試。",
      createError: "無法發起研究，請稍後再試。"
    }
  },
  privateAgentWorkspace: {
    recovery: {
      queueLabel: "回合佇列",
      replyDelivered: "回覆已送達",
      ended: "本輪已結束，無回覆",
      withdrawn: "執行前已停止",
      stopPending: "已請求停止，正在核對執行",
      checking: "正在核對執行狀態",
      needsAttention: "尚無回覆，需要處理",
      notStarted: "本輪未啟動",
      messageHeld: "訊息暫存中，需要處理",
      messageRefused: "訊息已被拒絕",
      cleanupUnknown: "執行已結束，清理狀態未確認",
      retryTurn: "重試本輪",
      endTurn: "結束本輪",
      stopAndSend: "停止並傳送",
      retryPending: "重送同一請求",
      pendingRequest: "回應尚未確認。將使用已儲存的身分和目標重送同一請求。",
      queueFull: "佇列已滿。請先結算一個回合，再傳送新輸入。",
      actionConflict: "目標已變更。請重新整理後再選擇操作。",
      storageFailure: "請求未傳送：瀏覽器儲存不可用或記錄無效。請重新整理核對。",
      queueCount: (count) => `未結算 ${count} / 8 輪`
    },
    ariaLabel: "Private Agent 工作區",
    conversationsAriaLabel: "Private Agent 對話",
    facetsAriaLabel: "Private Agent 工作檢視",
    chat: "聊天",
    research: "研究",
    recentConversations: "最近對話",
    noConversations: "尚未有對話。",
    loadingConversations: "正在載入對話",
    newConversation: "新增對話",
    creatingConversation: "正在建立…",
    messageCount: (count) => `${count} 則訊息`,
    identitySubtitle: "在你的電腦上工作，回答只留在你這裡。",
    deviceLine: (deviceName, state) => `在 ${deviceName} 上執行 · ${state}`,
    computerState: {
      online: "上線中",
      offline: "不在線上",
      revoked: "已移除",
      missing: "未連線"
    },
    selectConversationTitle: "開始或選擇一個對話",
    selectConversation: "從左邊選一個對話，或者新增一個。",
    setupTitle: "先設定你的助理",
    profileRequired: "先幫助理取個名字，並挑一台電腦給它用，然後再開始對話。",
    computerOfflineTitle: "你的電腦不在線上",
    updateTitle: "電腦上的助理需要更新或登入",
    signInTitle: "登入後使用助理",
    signIn: "登入後檢視或開始和助理的對話。",
    openComputers: "查看電腦",
    loadingConversation: "正在載入對話",
    transcriptAriaLabel: "對話記錄",
    emptyTranscript: "問點什麼吧，例如：幫我整理一下最近和 Acme 的往來。",
    composerLabel: "傳訊息給助理",
    composerPlaceholder: "傳訊息給助理…",
    send: "傳送",
    sending: "傳送中…",
    checkExecution: "重新檢查",
    cancelTurn: "停止",
    cancelingTurn: "正在停止…",
    elapsed: (value) => `已用 ${value}`,
    progressThinking: "正在思考",
    progressUsingTool: "正在使用工具",
    progressWaitingApproval: "等待你確認",
    boundDeviceRevokedNotice: "這個對話用的電腦已經無法使用，請新增對話。",
    readiness: {
      profile_missing: "先幫助理取個名字，並挑一台電腦給它用，然後再開始聊天。",
      device_missing: "助理原本用的那台電腦已經不在你的清單裡了。去「電腦」重新連線，或者挑一台已連線的電腦。",
      device_revoked: "助理原本用的那台電腦已被移除。去「電腦」挑一台已連線的電腦。",
      device_offline: "助理所在的電腦不在線上。讓它上線後再試。",
      agent_upgrade_required: (installedRelease) =>
        installedRelease
          ? `電腦上的 Salesko Agent 版本過舊（${installedRelease}），請更新後再試。`
          : "電腦上的 Salesko Agent 版本過舊，請更新後再試。",
      runtime_missing: "電腦上還沒安裝 Claude 或 Codex。安裝並登入後再試。",
      runtime_not_authenticated: "電腦上的 Claude 或 Codex 還沒登入。在那台電腦的終端機裡登入後再試。",
      toolset_not_configured: "電腦上的 Salesko Agent 需要更新並重新啟動。",
      runtime_unsupported_for_tools: "這個對話用的模型工具不支援讀取 Salesko 資料。請新增對話。",
      profile_identity_unavailable: "這個對話的助理已經不在了。重新設定同一個助理後再傳送。"
    },
    agent: "助理",
    you: "你",
    youInitial: "你",
    turnStatus: {
      blocked: "等待處理",
      queued: "排隊中",
      dispatching: "正在聯絡你的電腦",
      running: "正在處理",
      cancel_requested: "正在停止",
      succeeded: "已完成",
      failed: "失敗",
      canceled: "已停止"
    },
    errors: {
      activeConflict: "助理還在處理上一則訊息，等它回覆後再傳。",
      profileRequired: "先把助理設定好，再開始對話。",
      replayGap: "對話記錄需要重新整理。",
      reconcileFailed: "暫時無法確認助理的狀態，請重試。",
      chatPaused: "聊天服務已暫時暫停，請稍後重試。",
      requestFailed: "助理暫時無法使用，請稍後再試。"
    },
    researchEmptyTitle: "還沒有研究任務",
    researchEmpty: "打開一個人物或商機，讓助理去研究。",
    targetUnavailable: "研究目標暫時無法使用",
    contextAriaLabel: "此對話已載入的人脈、公司與商機",
    touchedEntitiesTitle: "此對話已載入的人脈、公司與商機",
    touchedEntitiesEmpty: "助理回答時查看過的人脈、公司和商機會列在這裡。",
    touchedEntitiesError: "這些名稱讀取失敗，對話本身不受影響。",
    touchedEntityUnavailable: "無法使用",
    openEntityInGraph: (label) => `在圖譜裡開啟 ${label}`,
    detailTabsAriaLabel: "Private Agent 側欄檢視",
    detailTab: "詳情",
    conversationTab: "本次對話"
  },

  dataTools: {
    ariaLabel: "進階資料工具",
    title: "進階資料工具",
    import: "匯入",
    export: "匯出",
    csvChangeSet: "CSV 變更集",
    jsonFrame: "JSON 畫面",
    obsidianFiles: "Obsidian 檔案",
    activeFrame: (frameRecordId) => `目前畫面 ${frameRecordId}`,
    needsCommittedFrame: "從已提交工作台畫面開啟設定，才能啟用 frame-scoped 工具。"
  },
  tenantAdmin: {
    workspaceAriaLabel: "工作台",
    workspaceTitle: "工作台",
    signInWorkspace: "登入以管理你的工作台。",
    loadingWorkspace: "正在載入工作台",
    workspaceName: "工作台名稱",
    saveName: "儲存名稱",
    workspaceNameSaved: "工作台名稱已儲存。",
    agentSettingsAriaLabel: "Agent 設定",
    agentDefaults: "Generic Agent 預設值",
    signInAgentDefaults: "登入以管理 Generic Agent 預設值。",
    checkingAccess: "正在檢查付費方案，然後解鎖 Generic Agent 預設值。",
    defaultsFrozen: "方案可驗證前，Generic Agent 預設值保持鎖定。",
    defaultsLocked: "啟用付費訂閱後可解鎖 Generic Agent 預設值。",
    loadingDefaults: "正在載入 Generic Agent 預設值",
    language: "語言",
    region: "區域",
    timezone: "時區",
    saveDefaults: "儲存 Generic Agent 預設值",
    defaultsSaved: "Generic Agent 預設值已儲存。",
    dataExportAriaLabel: "資料匯出",
    dataExport: "資料匯出",
    dataExportDescription: "下載工作台所有資料的 ZIP：圖譜、筆記與 audit trail。",
    exportZip: "匯出 ZIP",
    exportStarted: "工作台 ZIP 匯出已開始。",
    requestFailed: "工作台請求失敗。",
    locales: {
      en: "English",
      "zh-CN": "简体中文",
      "zh-TW": "繁體中文"
    }
  },
  canonicalRegistry: {
    eyebrow: "標準登錄",
    allTitle: "標準身份",
    count: (count) => `${count} 個租戶身份`,
    signedInRegistry: "登入後的租戶登錄",
    signIn: "使用租戶 session 登入後，可瀏覽已提交畫面中的標準身份。",
    unavailable: "標準登錄目前無法使用。",
    loading: "正在載入標準身份…",
    noSearchMatch: "目前搜尋沒有符合的標準身份。",
    empty: "尚無標準身份。",
    openIdentity: "開啟身份",
    openIdentityAriaLabel: (label) => `開啟 ${label}`,
    projectedCounts: (nodeCount, frameCount) => `${nodeCount} 個投影節點，跨 ${frameCount} 個畫面`,
    columns: {
      actions: "操作",
      confidence: "信心度",
      frames: "畫面",
      identity: "身份",
      lastSeen: "最後出現",
      nodes: "節點",
      type: "類型"
    }
  },
  mobile: {
    graph: "圖譜",
    inbox: "收件匣",
    you: "你",
    listView: "列表",
    graphCaption: "點選節點；越大的點代表連結越多",
    graphNodeAriaLabel: (label, typeLabel) => `${label}，${typeLabel}`,
    graphSelfNodeAriaLabel: (label) => `${label}，你`,
    primaryNavAriaLabel: "主導覽",
    notificationsTitle: "通知",
    captureMeeting: "擷取會議",
    selectedEntity: "已選實體",
    close: "關閉",
    clearSearch: "清除搜尋",
    relationshipReminder: "關係提醒",
    relationshipReminderBody: (sourceLabel, targetLabel, days) => `${sourceLabel} -> ${targetLabel} 已超過 touchpoint window ${days} 天。`,
    opportunitySignal: "商機",
    contractEmit: "Agent 會生成節點、關係、時間線與視角。",
    generatingGraphFrame: "正在生成圖譜畫面…",
    generateGraphFrame: "生成圖譜畫面",
    noDatedEvents: "此畫面尚無帶日期的事件。",
    undated: "未標日期",
    linkedEntitiesCount: (count) => `已連結實體 · ${count}`,
    thisWeek: "跨全部已儲存圖譜紀錄",
    opportunityHero: "處理新商機、儲存值得推進的項目，並加入未排程跟進。",
    activeSignalsAcross: (opportunityCount) => `目前顯示 ${opportunityCount} 個商機`,
    opportunityFeed: "商機收件箱",
    signInSignalsBody: "登入後，你的私人商機收件箱會顯示在這裡。",
    emptySignalsBody: "目前沒有待處理或已儲存的商機。",
    updatedAt: (date) => `更新於 ${date}`,
    evidenceCount: (count) => `${count} 條 evidence`,
    entityCount: (count) => `${count} 個實體`,
    frame: "畫面",
    notesCaptured: "你擷取的筆記。Agent 已將其結構化為圖譜節點。",
    committedFrames: "已提交畫面",
    current: "目前",
    frameNodeSummary: (nodeCount, date) => `${nodeCount} 個節點 · ${date}`,
    noCommittedFrames: "尚無已提交畫面",
    signInToSync: "登入以同步租戶圖譜存取",
    installedOnThisDevice: "已安裝在此裝置",
    installSalesko: "安裝 Salesko",
    installedPwaDescription: "以全螢幕開啟 · 可離線使用",
    installReadyDescription: "加入主畫面：全螢幕、可離線",
    installFallbackDescription: "Share -> Add to Home Screen 以取得 app 體驗",
    install: "安裝",
    mcpAccess: "MCP 存取",
    signInRequired: "需要登入",
    proPlanRequired: "需要 Pro 方案",
    signalsAndReminders: "訊號與提醒",
    tenantFeedActive: "租戶 feed 已啟用",
    inputLanguage: "輸入語言",
    privacy: "私隱",
    localPreview: "本機預覽",
    graphCachedOffline: "你的圖譜已快取，無連線也可閱讀。",
    markAllRead: "全部標為已讀",
    refreshingSignals: "正在刷新訊號…",
    noNotificationsTitle: "尚無通知",
    notificationsEmpty: "關係提醒與商機訊號會顯示在這裡。",
    browserDefault: "瀏覽器預設",
    noMatchesFor: (query) => `找不到「${query}」的結果`
  },
  connectors: {
    navigationAriaLabel: "連接器導覽",
    categoriesAriaLabel: "連接器分類",
    brandLabel: "連接器",
    eyebrow: "Integrations",
    title: "App Connectors",
    description: "雙向進入同一張圖譜：向 Agent 發訊息以捕捉筆記，或讓它讀取你已使用的帳戶。",
    searchPlaceholder: "搜尋連接器…",
    noMatchesTitle: "沒有符合項目",
    noMatchesBody: (categoryLabel) => `沒有 ${categoryLabel} 連接器符合搜尋。`,
    tierPro: "Pro",
    comingSoon: "即將推出",
    premium: "Premium",
    upgradeToUnlock: "升級以解鎖",
    available: "可用",
    connect: "連接",
    connected: "已連接",
    manage: "管理",
    disconnect: "中斷連接",
    connectError: "無法更新 Gmail 連接，請再試一次。",
    calendarConnectError: "行事曆連接失敗，請再試一次。",
    connectedAccount: (email) => `已連接：${email}`,
    oneTimeConnected: "Gmail 已連接。",
    oneTimeError: "Gmail 連接失敗，請再試一次。",
    syncNow: "立即同步",
    syncing: "同步中…",
    syncQueued: "同步已排入佇列，新的 lead 稍後就會出現。",
    calendarSyncQueued: "行事曆同步已加入佇列。",
    syncCompleted: "同步完成。",
    syncError: "無法啟動 Gmail 同步，請再試一次。",
    calendarSyncError: "無法啟動行事曆同步，請再試一次。",
    lastSyncedAt: (date) => `上次同步：${date}`,
    lastSyncFailed: (message) => (message ? `上次同步失敗：${message}` : "上次同步失敗。"),
    reconnectRequired: "Google 授權已被撤銷，請重新連接以繼續同步 lead。",
    direction: {
      inbound: "Inbound · 筆記擷取",
      outbound: "Outbound · 資料來源"
    },
    lockCopy: {
      signed_out: "登入以解鎖 premium connectors。",
      checking: "正在檢查 paid Sidecar access 以解鎖 premium connectors。",
      verify_failed: "在 Sidecar access 可驗證前，premium connectors 會保持鎖定。",
      locked: "啟用 paid Sidecar subscription 後可解鎖 premium connectors。"
    },
    categories: {
      messaging: {
        eyebrow: "筆記擷取",
        label: "Messaging",
        blurb: "直接在這些渠道向你的個人 Agent 發訊息；它會在同一串對話回覆。"
      },
      email: {
        eyebrow: "Inbox intelligence",
        label: "Email",
        blurb: "讓 Agent 讀取郵件，將 threads 變成可搜尋的關係筆記。"
      },
      contacts: {
        eyebrow: "人物同步",
        label: "Contacts",
        blurb: "自動將你已認識的人帶入圖譜。"
      },
      calendar: {
        eyebrow: "會議訊號",
        label: "Calendar",
        blurb: "同步行事曆，讓每場會議與參加者都成為圖譜中的關係訊號。"
      }
    },
    items: {
      telegram: {
        displayName: "Telegram",
        tagline: "在 Telegram 向 Agent 發訊息即可隨手擷取筆記；它會在同一串對話回覆。"
      },
      wechat: {
        displayName: "微信 WeChat",
        tagline: "向 Agent 發送微信訊息以記錄筆記或聯絡人；它會在原對話回覆。"
      },
      line: {
        displayName: "Line",
        tagline: "向 Agent 發送 Line 訊息以擷取會議筆記；回覆會回到同一個聊天。"
      },
      gmail: {
        displayName: "Gmail",
        tagline: "讓 Agent 讀取並整理 Gmail，將郵件 threads 變成可搜尋的關係筆記。"
      },
      "microsoft-email": {
        displayName: "Microsoft Email",
        tagline: "讓 Agent 讀取 Outlook 郵件，將對話變成可搜尋的關係信號。"
      },
      "google-contacts": {
        displayName: "Google Contacts",
        tagline: "同步 Google Contacts，讓已認識的人自動出現在圖譜中。"
      },
      "google-calendar": {
        displayName: "Google Calendar",
        tagline: "同步 Google 行事曆，讓會議與參加者作為 event 訊號流入圖譜。"
      },
      "microsoft-calendar": {
        displayName: "Microsoft Calendar",
        tagline: "同步 Outlook / Microsoft 行事曆，讓會議與參加者流入圖譜。"
      },
      "apple-calendar": {
        displayName: "Apple Calendar",
        tagline: "即將推出 — 透過 CalDAV 同步 iCloud 行事曆。"
      }
    },
    replyTemplate: {
      eyebrow: "回覆自動化",
      heading: "Gmail 回覆模板",
      subtitle: "給 Agent 一個用戶認可的模板庫，郵件回覆啟用後只從這裡選擇。",
      newTemplate: "新增模板",
      emptyTitle: "還沒有模板",
      enabledLabel: "允許 Agent 使用",
      enabledHint: "只有啟用的模板會進入 Agent 選擇範圍。",
      requiresReviewLabel: "需要人工確認",
      requiresReviewHint: "在最終發送流程明確批准前，建議保持開啟。",
      nameLabel: "名稱",
      namePlaceholder: "詢價郵件",
      descriptionLabel: "使用場景",
      descriptionPlaceholder: "當潛在客戶詢問價格或採購流程時使用。",
      subjectLabel: "主旨",
      subjectPlaceholder: "Re: {{subject}}",
      bodyLabel: "正文",
      bodyPlaceholder: "Hi {{senderName}},\n\n感謝聯絡。我會查看後盡快跟進。\n\nBest,\n{{myName}}",
      variablesHint: "可用變數：{{senderName}}、{{senderEmail}}、{{subject}}、{{myName}}",
      create: "建立模板",
      creating: "建立中…",
      save: "保存模板",
      saving: "保存中…",
      saved: "模板已保存。",
      createError: "無法建立模板，請再試一次。",
      saveError: "無法保存模板，請再試一次。",
      loadError: "無法載入已保存模板。",
      updatedAt: (date) => `更新於 ${date}`
    },
    candidates: {
      eyebrow: "Inbox intelligence",
      heading: "Lead 候選",
      subtitle: "Agent 從信箱中判斷出的真實潛在客戶，等你確認。",
      emptyTitle: "目前沒有 lead 候選",
      emptyBody: "同步你的 Gmail 帳號，Agent 會在這裡列出判斷為真實潛在客戶的郵件。",
      confidence: (percent) => `信心度 ${percent}%`,
      draftReply: "起草回覆",
      draftReplyPending: "起草中…",
      draftReplyCreated: "Gmail 草稿已建立。發送前請在 Gmail 中檢查。",
      draftReplyError: "無法建立 Gmail 草稿，請再試一次。",
      draftReconnectRequired: "請重新連接 Gmail 以授權建立草稿。",
      confirm: "確認",
      confirmPending: "確認中…",
      confirmError: "無法確認此候選項，請再試一次。",
      dismiss: "忽略",
      dismissPending: "忽略中…",
      dismissError: "無法忽略此候選項，請再試一次。",
      needsFrame: "請先開啟工作台並儲存一張圖譜畫面，再確認候選項。",
      frameConflictRetry: "確認過程中圖譜發生了變化，請重新整理後再試一次。"
    }
  },
  schedule: {
    eyebrow: "唯讀議程",
    title: "行程",
    range: (from, to) => `${from} 至 ${to}`,
    items: (count) => `${count} 項`,
    signInToLoad: "登入後查看你的私人行程議程。",
    loading: "正在載入行程…",
    unavailable: "行程暫時無法使用。",
    calendarTitle: "日曆事件",
    calendarEmpty: "此時間範圍內沒有日曆事件。",
    untitledEvent: "未命名日曆事件",
    timeUnavailable: "時間無法使用",
    followUpsTitle: "未排程跟進",
    followUpsEmpty: "沒有等待排程的跟進。",
    followUpRequested: (date) => `要求跟進於 ${date}`,
    remindersTitle: "待重聯關係",
    remindersEmpty: "沒有需要關注的冷卻關係。",
    lastSeen: (date) => `最近聯絡 ${date}`,
    staleByDays: (days) => `已逾期 ${days} 天`,
    truncated: "此有界回應之外還有更多項目。",
    close: "關閉行程"
  },
  currentLeads: {
    eyebrow: "跟進管道",
    title: "目前",
    activeCount: (count) => `${count} 位在跟進`,
    emptyTitle: "目前還沒有內容",
    emptyBody: "確認一個郵件 lead 候選，或將任意節點標記為目前，即可在這裡追蹤。",
    viewInGraph: "在圖譜中檢視",
    removeFromCurrent: "移出目前",
    sourceEmail: "來自郵件"
  },
  mcpConsent: {
    title: "授權 MCP 存取",
    description: "選擇這個 MCP 客戶端可以存取你 Salesko 圖譜的範圍，然後繼續以授予存取權限。",
    scopeGraphReadLabel: "讀取你的圖譜",
    scopeGraphReadHint: "搜尋並讀取人物、公司、事件與商機。",
    scopeCalendarReadLabel: "讀取你的行事曆",
    scopeCalendarReadHint: "列出並搜尋即將到來的行事曆事件。",
    scopeGraphWriteLabel: "提議圖譜變更",
    scopeGraphWriteHint: "提交待人工審核的變更提議；不會自動套用任何變更。",
    continue: "繼續",
    signInBody: "登入以授權這個 MCP 客戶端存取你的 Salesko 圖譜。",
    invalidLinkHeading: "授權連結無效",
    invalidLinkBody: "這個 MCP 授權連結缺失或無效，請從你的 MCP 客戶端重新開始。",
    authNotConfiguredBody: "這個 Salesko 部署尚未設定登入方式，因此無法授予 MCP 存取權限。",
    issuanceFailed: "無法授予 MCP 存取權限，請再試一次。"
  },
  meetingPreparation: {
    ariaLabel: "會議準備",
    title: "會議準備",
    prepareAction: "準備簡報",
    prepareActionTitle: "為此事件產生會議簡報",
    preparePending: "準備中…",
    idleBody: "為這場會議產生 AI 簡報：目標、與會者、關係訊號與建議提問。",
    loading: "正在準備會議簡報…",
    successHeading: "會議簡報",
    goalTitle: "目標",
    participantsTitle: "與會者與已知背景",
    knownContextEmpty: "除與會者外暫無更多背景資訊。",
    relationshipSignalsTitle: "關係訊號",
    relationshipSignalsEmpty: "你的圖譜中暫無關係訊號。",
    watchoutsTitle: "注意事項",
    watchoutsEmpty: "未識別到需要注意的事項。",
    suggestedQuestionsTitle: "建議提問",
    suggestedCloseTitle: "建議收尾",
    sourcesTitle: "來源",
    sourcesTruncated: "此有界回應之外還有更多來源。",
    sourceCalendarEvent: "行事曆事件",
    sourceCalendarAttendee: "行事曆與會者",
    sourceGraphEdge: "關係",
    sourceEvidence: "證據",
    sourceFrameProvenance: (frameId, acceptedAt) => `圖譜幀 ${frameId} · 採納於 ${acceptedAt}`,
    gapsTitle: "缺口",
    gapsEmpty: "未發現缺口——此簡報證據完整。",
    gapNoGraphPersonMatch: (participantLabel) => `未在圖譜中找到 ${participantLabel} 的匹配人物。`,
    gapNoRelationshipEvidence: "你的圖譜中暫無可用的關係證據。",
    gapNoOpportunityEvidence: "你的圖譜中暫無可用的商機證據。",
    gapContextTruncated: (collections) => `部分背景資訊被截斷：${collections}。`,
    gapOptionalCalendarDetailUnavailable: (detail) => `此行事曆事件缺少${detail}。`,
    calendarDetailLabels: {
      summary: "摘要",
      location: "地點",
      endAt: "結束時間",
      organizer: "主辦人"
    },
    truncatedCollectionLabels: {
      calendarAttendees: "行事曆與會者",
      graphPeople: "圖譜人物",
      graphContextNodes: "圖譜背景",
      graphEdges: "圖譜關係",
      opportunities: "商機",
      evidence: "證據"
    },
    errorEntitlement: "會議準備功能需要付費訂閱，請升級後使用。",
    errorNotEligible: "此事件不符合產生會議簡報的條件。",
    errorStaleEvent: "此行事曆事件已過期，請同步行事曆後重試。",
    errorModelError: "暫時無法產生簡報，請重試。",
    errorGeneric: "無法準備此會議簡報，請重試。"
  }
} satisfies Dictionary;
